/**
 * useDescriptiveNote Hook
 * Handles descriptive HPI note generation
 */

import { useState, useCallback, useEffect } from 'react';
import { dateStrToAgeMonths, dateStrToAgeDays, PatientDobFormat } from '@cyren/common-lib';
import { toast } from 'react-toastify';
import type { UseDescriptiveNoteReturn, ReportStateType, SurveyStateType, VisitType } from '../types';
import { customInstance } from '../../../../api/custom-instance';
import {
  buildBaseDescriptivePrompt,
  DESCRIPTIVE_SAFETY_INSTRUCTION,
  getDescriptiveLanguageInstruction,
  getDescriptiveLocaleStyleInstruction,
} from '../../../../shared/utils/descriptivePrompt';
import { extractDescriptiveSections } from '../../../../shared/utils/extractDescriptiveSections';
import {
  DESCRIPTIVE_REPORT_ERROR_MESSAGE,
  requireDescriptiveReportText,
} from '../../../../shared/utils/descriptiveReportResult';

interface UseDescriptiveNoteProps {
  reportState: ReportStateType;
  surveyState: SurveyStateType;
  visit: VisitType | undefined;
  shortId?: string;
  defaultLanguage?: string;
  onReportStateUpdate?: (updater: (state: ReportStateType) => ReportStateType) => void;
  onVisitNotFound?: () => Promise<VisitType | null>;
}

function stripCyrenTags(value: string | undefined): string {
  return String(value || '')
    .replace(/<cyrenvalue>/g, '')
    .replace(/<\/cyrenvalue>/g, '')
    .trim();
}

function toTitleFromKey(value: string): string {
  return value
    .replace(/^q-/, '')
    .replace(/^sx-/, '')
    .replace(/-/g, ' ')
    .split(' ')
    .map((word) => (word ? `${word.charAt(0).toUpperCase()}${word.slice(1)}` : ''))
    .join(' ')
    .trim();
}

function buildReportSnapshot(
  reportState: ReportStateType,
  sex: string,
  ageText: string
): string {
  const sections: string[] = [];
  const systemValueMap = reportState.systemValueMap || {};
  const firstName = String(systemValueMap.firstName || systemValueMap.firstname || '').trim();
  const lastName = String(systemValueMap.lastName || systemValueMap.lastname || '').trim();
  const patientLine = [sex, ageText, `${firstName} ${lastName}`.trim()].filter(Boolean).join(' ');
  if (patientLine) {
    sections.push(`Patient info\n${patientLine}`);
  }

  const cc = String(systemValueMap.cc || '').trim();
  if (cc) {
    sections.push(`CC\n${cc}`);
  }

  const presentIllness = reportState.presentIllness || [];
  if (presentIllness.length > 0) {
    const hpiLines: string[] = ['HPI'];
    presentIllness.forEach((phi) => {
      if (phi.onsetDate) {
        hpiLines.push(`${phi.onsetDate}${phi.relativeOnset ? ` - ${phi.relativeOnset}` : ''}`);
      }
      const mainLine = stripCyrenTags(phi.lineOutput);
      if (mainLine) {
        hpiLines.push(`   ${mainLine}`);
      }
      phi.sublinesOutput?.forEach((subline) => {
        const text = stripCyrenTags(subline);
        if (text) {
          hpiLines.push(`   ${text}`);
        }
      });
    });
    if (hpiLines.length > 1) {
      sections.push(hpiLines.join('\n'));
    }
  } else {
    const hpiLines: string[] = ['HPI'];
    const sxTreeStates = (reportState.treeStates || []).filter((ts) =>
      String(ts.treeKey || '').startsWith('sx-')
    );
    sxTreeStates.forEach((ts) => {
      if (!ts.answerMap) return;
      Object.entries(ts.answerMap).forEach(([questionKey, answer]) => {
        const values = [
          ...(answer.answerLabels || []),
          ...((answer.answerValues || []).map((v) => String(v || '').trim()).filter(Boolean)),
        ];
        if (!values.length) return;
        const question = toTitleFromKey(questionKey);
        hpiLines.push(`   ${question}: ${values.join(', ')}`);
      });
    });
    if (hpiLines.length > 1) {
      sections.push(hpiLines.join('\n'));
    }
  }

  return sections.join('\n\n');
}

function normalizeLocaleKey(value?: string | null): string {
  const locale = String(value || '')
    .trim()
    .toLowerCase()
    .replace('_', '-');

  if (!locale) return 'english';
  if (locale === 'ko' || locale.startsWith('ko-') || locale === 'korean') return 'korean';
  if (locale === 'ja' || locale.startsWith('ja-') || locale === 'japanese') return 'japanese';
  if (locale === 'zh' || locale.startsWith('zh-') || locale === 'chinese') return 'chinese';
  if (locale === 'es' || locale.startsWith('es-') || locale === 'spanish') return 'spanish';
  if (locale === 'en' || locale.startsWith('en-') || locale === 'english') return 'english';
  return locale;
}

function sanitizeDescriptiveOutput(text: string, rawCc?: string): string {
  const cleaned = String(text || '')
    .replace(/\s*\[\s*Auto-translated[^\]]*]\s*/gi, ' ')
    .replace(/\s*\[\s*Korean[^\]]*]\s*/gi, ' ')
    .replace(/\s*\[\s*한국어[^\]]*]\s*/g, ' ')
    .replace(/\s*\[\s*자동 번역 원문[^\]]*]\s*/gi, ' ')
    .replace(/\s*\[[^\]]*자동\s*번역됨\s*]\s*/gi, ' ')
    // Remove inline language annotations like "[한국어 머리가 아파요]" or "(Korean: ...)"
    .replace(/\s*\[한국어\s+[^\]]*]\s*/g, ' ')
    .replace(/\s*\(Korean:\s*[^)]*\)\s*/gi, ' ')
    .replace(/\s*\(한국어:\s*[^)]*\)\s*/g, ' ')
    // Remove redundant parenthetical translations of Korean terms
    .replace(/갑작스럽게\s*\(갑작스러운 발병\)/g, '갑자기')
    .replace(/갑작스럽게\s*\([Aa]brupt\s+onset\)/g, '갑자기')
    // Remove English parenthetical after Korean medical terms: "두통(Headache)" → "두통"
    .replace(/([\uAC00-\uD7A3]{2,})\s*\([A-Z][a-z]+(?:\s+[a-z]+)*\)/g, '$1')
    // Remove Korean parenthetical restating same term: "두통(두통)" → "두통"
    .replace(/([\uAC00-\uD7A3]{2,})\s*\(\1\)/g, '$1')
    .replace(/\r\n/g, '\n')
    .replace(/\s+(PI:)/g, '\n$1')
    .replace(/\s+(PMHx:)/g, '\n\n$1')
    .replace(/\s+(DHx:)/g, '\n$1')
    .replace(/\s+(Allergy:|알레르기:)/g, '\n$1')
    .replace(/\s+(Medication:|약물 복용)/g, '\n$1')
    .replace(/^CC:\s*([^\n]+?)\s+PI:/m, 'CC: $1\nPI:');

  const lines = cleaned
    .split('\n')
    .map((line) => line.replace(/[ \t]+/g, ' ').trim())
    .reduce<string[]>((acc, line) => {
      if (!line) {
        if (acc.length > 0 && acc[acc.length - 1] !== '') {
          acc.push('');
        }
        return acc;
      }
      acc.push(line);
      return acc;
    }, []);

  while (lines.length > 0 && lines[0] === '') lines.shift();
  while (lines.length > 0 && lines[lines.length - 1] === '') lines.pop();

  if (rawCc && rawCc.trim()) {
    const ccLine = `CC: ${rawCc.trim()}`;
    const ccIndex = lines.findIndex((line) => /^CC:/i.test(line));
    if (ccIndex >= 0) {
      lines[ccIndex] = ccLine;
    } else {
      lines.unshift(ccLine);
    }
  }

  const medicationLineRegex = /^(Medication:|약물:|약물 복용:?|약물\s*-)/i;
  const medicationLineIndexes = lines
    .map((line, index) => ({ line, index }))
    .filter(({ line }) => medicationLineRegex.test(line));

  if (medicationLineIndexes.length > 1) {
    const hasCanonicalNoneLine = medicationLineIndexes.some(({ line }) =>
      /^(Medication:\s*None|약물 복용:?\s*없음)/i.test(line),
    );
    if (hasCanonicalNoneLine) {
      const nextLines = lines.filter((line) => !medicationLineRegex.test(line));
      nextLines.push('약물 복용: 없음');
      return nextLines.join('\n');
    }
  }

  return lines.join('\n').replace(/\n(PMHx:)/g, '\n\n$1');
}

export function useDescriptiveNote({
  reportState,
  surveyState,
  visit,
  shortId,
  defaultLanguage = 'en',
  onReportStateUpdate,
  onVisitNotFound,
}: UseDescriptiveNoteProps): UseDescriptiveNoteReturn {
  const [descriptiveReport, setDescriptiveReport] = useState<string | null>(
    reportState.descriptiveReport || null
  );
  const [isGenerating, setIsGenerating] = useState(false);

  // Sync with reportState changes
  useEffect(() => {
    if (reportState.descriptiveReport) {
      setDescriptiveReport(reportState.descriptiveReport);
    }
  }, [reportState.descriptiveReport]);

  const generateDescriptiveNote = useCallback(async () => {
    if (!visit?.uuid) {
      console.warn('No visit UUID available for generating descriptive note');
      return;
    }

    setIsGenerating(true);

    try {
      // Extract patient info from systemValueMap
      const systemValueMap = reportState.systemValueMap || {};
      const cc = (systemValueMap.cc as string) || '';
      const sex = ((systemValueMap.sex as string) || '').replace('o-', '') || 'Gender is Not provided';
      const dob =
        (systemValueMap.dateOfBirth as string) ||
        (systemValueMap.dob as string) ||
        (systemValueMap.age as string) ||
        '';

      // Calculate age
      let age: number | null = null;
      let monthAge: number | null = null;
      let dayAge: number | null = null;

      if (dob) {
        const birthDate = new Date(dob);
        const today = new Date();
        age = today.getFullYear() - birthDate.getFullYear();

        const monthDifference = today.getMonth() - birthDate.getMonth();
        const dayDifference = today.getDate() - birthDate.getDate();
        if (monthDifference < 0 || (monthDifference === 0 && dayDifference < 0)) {
          age--;
        }

        monthAge = dateStrToAgeMonths(dob, { inputFormat: PatientDobFormat });
        dayAge = dateStrToAgeDays(dob, { inputFormat: PatientDobFormat });
      }

      // Get age string
      const getAgeString = () => {
        if (age !== null && age >= 2) {
          return `${age} years old`;
        }
        if (monthAge !== null && monthAge >= 1) {
          return `${monthAge} months old`;
        }
        if (dayAge !== null && monthAge !== null && monthAge < 1) {
          return `${dayAge} days old`;
        }
        return 'Age is Not provided';
      };

      const { hpiData, allergyData, pmhData, medicationData } = extractDescriptiveSections(reportState);

      // Prefer organization default language first, then fall back to report locale.
      const targetLocale = normalizeLocaleKey(defaultLanguage || reportState.locale);

      const englishPrompt = buildBaseDescriptivePrompt({
        gender: sex,
        ageString: getAgeString(),
        cc,
        pmhData,
        hpiData,
        medicationData,
        allergyData,
      });

      const languageInstruction = getDescriptiveLanguageInstruction(targetLocale);
      const localeStyleInstruction = getDescriptiveLocaleStyleInstruction(targetLocale);
      const reportSnapshot = buildReportSnapshot(reportState, sex, getAgeString());
      const prompt = `${englishPrompt}

REPORT SNAPSHOT FROM UI (HIGH-PRIORITY FACTS):
${reportSnapshot || 'None'}

When there is any conflict, prioritize this REPORT SNAPSHOT and include those facts explicitly in chronological order.

${languageInstruction}
${localeStyleInstruction}
${DESCRIPTIVE_SAFETY_INSTRUCTION}`;

      const requestDescriptiveReport = async (visitUuid: string) =>
        customInstance<{ text: string }>({
          url: `/patient-visits/generate-descriptive-report`,
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          data: {
            visitId: visitUuid,
            prompt,
          },
        });

      let response: { text: string };
      try {
        response = await requestDescriptiveReport(visit.uuid);
      } catch (error) {
        const statusCode = (error as { response?: { status?: number } })?.response?.status;
        if (statusCode === 404 && onVisitNotFound) {
          console.warn('[useDescriptiveNote] generate-descriptive-report returned 404. Attempting visit recovery.', {
            shortId,
            visitId: visit?.id,
            visitUuid: visit?.uuid,
          });
          const recoveredVisit = await onVisitNotFound();
          const recoveredUuid = recoveredVisit?.uuid;
          if (!recoveredUuid) {
            throw error;
          }
          response = await requestDescriptiveReport(recoveredUuid);
        } else {
          throw error;
        }
      }

      const newText = requireDescriptiveReportText(response.text);

      const nextText = sanitizeDescriptiveOutput(newText, cc);
      setDescriptiveReport(nextText);

      // Update report state if callback provided
      onReportStateUpdate?.((state) => ({
        ...state,
        descriptiveReport: nextText,
      }));
    } catch (error) {
      console.error('Failed to generate descriptive note:', error);
      toast.error(DESCRIPTIVE_REPORT_ERROR_MESSAGE);
    } finally {
      setIsGenerating(false);
    }
  }, [reportState, visit?.id, visit?.uuid, shortId, onReportStateUpdate, defaultLanguage, onVisitNotFound]);

  return {
    descriptiveReport,
    isGenerating,
    generateDescriptiveNote,
    setDescriptiveReport,
  };
}
